import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import toast from 'react-hot-toast';
import { RequireOrg, VpState } from '../components/VelopulseShell';
import VpModal from '../components/VpModal';
import useLoad from '../utils/useLoad';
import { useOrgStore } from '../store/org';
import { hasModule } from '../auth/capabilities';
import { vpApi, errorMessage, fmtWan, OPP_STAGES, stageOf } from '../api/velopulse';

function OppCardBody({ opp }) {
  return (
    <>
      <div className="t">{opp.title}</div>
      {opp.summary && <div className="m">{opp.summary}</div>}
      <div className="f">
        {opp.tag ? <span className="vp-opp-tag">{opp.tag}</span> : <span />}
        <span className="amt">{opp.amount != null ? fmtWan(opp.amount) : '—'}</span>
      </div>
    </>
  );
}

function OppCard({ opp, onOpen }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: opp.id });
  return (
    <div
      ref={setNodeRef}
      className={`vp-opp${isDragging ? ' dragging' : ''}`}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      aria-label={`${opp.title}，階段：${stageOf(opp.stage).label}。按 Enter 開啟詳情`}
      onClick={() => onOpen(opp)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(opp); }}
    >
      <OppCardBody opp={opp} />
    </div>
  );
}

function StageColumn({ stage, items, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.key });
  return (
    <div ref={setNodeRef} className={`vp-col${isOver ? ' over' : ''}`}>
      <h3>{stage.label} <span>{items.length}</span></h3>
      {items.map((o) => <OppCard key={o.id} opp={o} onOpen={onOpen} />)}
    </div>
  );
}

function NewOpportunityModal({ recordId, onClose, onCreated }) {
  const org = useOrgStore((s) => s.currentOrg);
  const [form, setForm] = useState({ title: '', customer_name: '', amount: '', summary: '' });
  const [item, setItem] = useState(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canVehicle = hasModule(org, 'automotive');
  const canRealty = hasModule(org, 'real_estate');

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const rows = await vpApi.searchCatalog({ q: q.trim() || undefined });
        if (!cancelled) setHits(Array.isArray(rows) ? rows : []);
        if (recordId && !item) {
          const pre = (rows || []).find((r) => r.id === recordId && r.type === 'vehicle');
          if (pre && !cancelled) pickItem(pre);
        }
      } catch {
        if (!cancelled) setHits([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, recordId]);

  const pickItem = (row) => {
    setItem(row);
    setQ('');
    setHits([]);
    setForm((f) => ({
      ...f,
      title: f.title.trim() ? f.title : `${f.customer_name || ''} · ${row.label}`.replace(/^\s*·\s*/, '').trim(),
      amount: f.amount !== '' ? f.amount : (row.amount != null ? String(row.amount) : ''),
      summary: f.summary || (row.subtitle ? `關聯：${row.label}（${row.subtitle}）` : `關聯：${row.label}`),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('請填寫商機名稱。'); return; }
    const ok = window.confirm(`確定在「${org?.name}」建立商機「${form.title.trim()}」${item ? `，並關聯「${item.label}」` : ''}？\n\n同一車輛或物件可以有多筆不同顧客的商機。`);
    if (!ok) return;
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        amount: form.amount === '' ? null : Number(form.amount),
        stage: 'new',
      };
      if (item) {
        payload.item_type = item.type;
        payload.item_id = item.id;
        if (item.type === 'vehicle') payload.record_id = item.id;
      } else if (recordId) {
        payload.record_id = recordId;
        payload.item_type = 'vehicle';
      }
      const created = await vpApi.createOpportunity(payload);
      toast.success('已建立商機');
      onCreated(created);
    } catch (err) {
      setError(errorMessage(err, '建立失敗，請稍後再試。'));
    } finally { setSaving(false); }
  };

  return (
    <VpModal title="新增商機" onClose={onClose}>
      <form onSubmit={submit}>
        <p className="vp-muted" style={{ fontSize: 12, margin: 0 }}>將建立於組織：<strong style={{ color: 'var(--vp-ink)' }}>{org?.name}</strong> · 階段：新進線。同一品項可對多位顧客各建一筆商機。</p>
        {(canVehicle || canRealty) && (
          <div className="vp-field">
            <label htmlFor="no-item">關聯車輛或物件</label>
            {item ? (
              <div className="vp-okbox" style={{ marginTop: 0 }}>
                <div>
                  <strong>{item.label}</strong>
                  <div className="vp-muted">{item.type === 'realty' ? '房仲物件' : '車輛'}{item.subtitle ? ` · ${item.subtitle}` : ''}</div>
                </div>
                <button type="button" className="vp-btn" onClick={() => setItem(null)}>清除</button>
              </div>
            ) : (
              <>
                <input id="no-item" className="vp-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={canVehicle && canRealty ? '搜尋車輛或物件' : canRealty ? '搜尋物件' : '搜尋車輛'} />
                {hits.length > 0 && (
                  <ul className="vp-catalog-hits">
                    {hits.map((h) => (
                      <li key={`${h.type}-${h.id}`}>
                        <button type="button" onClick={() => pickItem(h)}>
                          <strong>{h.label}</strong>
                          <span className="vp-muted">{h.type === 'realty' ? '物件' : '車輛'}{h.subtitle ? ` · ${h.subtitle}` : ''}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
        <div className="vp-field"><label htmlFor="no-title">商機名稱</label><input id="no-title" className="vp-input" value={form.title} onChange={set('title')} placeholder="例如：王先生 · Camry" /></div>
        <div className="vp-row2">
          <div className="vp-field"><label htmlFor="no-cust">顧客</label><input id="no-cust" className="vp-input" value={form.customer_name} onChange={set('customer_name')} /></div>
          <div className="vp-field"><label htmlFor="no-amt">預估金額（NT$）</label><input id="no-amt" className="vp-input" type="number" value={form.amount} onChange={set('amount')} /></div>
        </div>
        <div className="vp-field"><label htmlFor="no-sum">摘要</label><textarea id="no-sum" className="vp-input" value={form.summary} onChange={set('summary')} /></div>
        {error && <div className="vp-error" role="alert">{error}</div>}
        <div className="vp-wiz-actions">
          <button type="button" className="vp-btn lg" onClick={onClose}>取消</button>
          <button type="submit" className="vp-btn lg primary" disabled={saving}>{saving ? '建立中…' : '確認建立'}</button>
        </div>
      </form>
    </VpModal>
  );
}

function OpportunityBoard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: opps, error, loading, reload, setData } = useLoad(() => vpApi.listOpportunities(), []);
  const [activeId, setActiveId] = useState(null);
  // 拖曳需先移動 6px（滑鼠）或長按（觸控），點一下仍可開啟詳情、手機仍可橫向滑動
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );
  const showNew = params.get('new') === '1';
  const closeNew = () => setParams({}, { replace: true });

  const onDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const opp = opps.find((o) => o.id === active.id);
    if (!opp || opp.stage === over.id) return;
    const prev = opp.stage;
    setData((list) => list.map((o) => (o.id === opp.id ? { ...o, stage: over.id } : o)));
    try {
      await vpApi.moveOpportunity(opp.id, over.id);
      toast.success(`已移至「${stageOf(over.id).label}」`);
    } catch (err) {
      setData((list) => list.map((o) => (o.id === opp.id ? { ...o, stage: prev } : o)));
      toast.error(errorMessage(err, '移動失敗，已還原。'));
    }
  };

  if (loading) return <VpState loading />;
  if (error) return <div className="vp-content"><div className="vp-card"><VpState text={errorMessage(error, '商機載入失敗。')} action={{ label: '重新載入', onClick: reload }} /></div></div>;

  const active = opps.find((o) => o.id === activeId);

  return (
    <div className="vp-content wide" style={{ overflow: 'hidden' }}>
      <div className="vp-bar">
        <span className="vp-muted" style={{ fontSize: 13 }}>共 {opps.length} 筆商機 · 拖曳卡片可變更階段</span>
        <button type="button" className="vp-btn primary" onClick={() => setParams({ new: '1' })}>新增商機</button>
      </div>
      <DndContext sensors={sensors} onDragStart={({ active: a }) => setActiveId(a.id)} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
        <div className="vp-board">
          {OPP_STAGES.map((s) => (
            <StageColumn key={s.key} stage={s} items={opps.filter((o) => o.stage === s.key)} onOpen={(o) => navigate(`/opportunities/${o.id}`)} />
          ))}
        </div>
        <DragOverlay>{active ? <div className="vp-opp" style={{ cursor: 'grabbing', boxShadow: '0 8px 24px rgba(15,23,42,.18)' }}><OppCardBody opp={active} /></div> : null}</DragOverlay>
      </DndContext>
      {showNew && (
        <NewOpportunityModal
          recordId={params.get('record')}
          onClose={closeNew}
          onCreated={(o) => { closeNew(); if (o?.id) setData((list) => [o, ...list.filter((x) => x.id !== o.id)]); else reload(); }}
        />
      )}
    </div>
  );
}

export default function OpportunityBoardPage() {
  return <RequireOrg><OpportunityBoard /></RequireOrg>;
}
